/*
  assets/component-mega-menu.js
  --------------------------------------------------------------------
  Behaviour layer for the redesigned header mega menu. Three concerns:

    1. Copy-to-clipboard for the offer code buttons.
    2. Postcode/town search + geolocate in the salons panel.
    3. Lazy-load the JustGiving widget the first time the LPT panel opens.

  Designed to be safe to include on every page:
    - All handlers delegated from document.body, so it doesn't matter
      whether the panel markup is present at script load.
    - Idempotent: re-running the file won't double-bind.

  Include in theme.liquid (or wherever you load assets) with:
    {{ 'component-mega-menu.js' | asset_url | script_tag }}

  Or with the `defer` attribute via:
    <script src="{{ 'component-mega-menu.js' | asset_url }}" defer></script>
*/

(function () {
  'use strict';

  if (window.__scMegaMenuInit) return; // guard against double-init
  window.__scMegaMenuInit = true;

  /* ================================================================
     1. Copy-to-clipboard for offer codes
     ============================================================== */
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('.sc-offer-row__copy');
    if (!btn) return;
    event.preventDefault();

    var code = btn.getAttribute('data-copy') || '';
    if (!code) return;

    var done = function () {
      btn.classList.add('is-copied');
      window.clearTimeout(btn.__copyTimer);
      btn.__copyTimer = window.setTimeout(function () {
        btn.classList.remove('is-copied');
      }, 1800);
    };

    var fail = function () {
      // Fallback: select the sibling <code> so the user can hit Cmd/Ctrl-C
      var codeEl = btn.parentElement && btn.parentElement.querySelector('.sc-offer-row__code');
      if (codeEl && window.getSelection) {
        var range = document.createRange();
        range.selectNodeContents(codeEl);
        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done).catch(function () {
        // Last-ditch: legacy execCommand path
        try {
          var ta = document.createElement('textarea');
          ta.value = code;
          ta.setAttribute('readonly', '');
          ta.style.position = 'absolute';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          done();
        } catch (e) {
          fail();
        }
      });
    } else {
      try {
        var ta2 = document.createElement('textarea');
        ta2.value = code;
        ta2.setAttribute('readonly', '');
        ta2.style.position = 'absolute';
        ta2.style.left = '-9999px';
        document.body.appendChild(ta2);
        ta2.select();
        document.execCommand('copy');
        document.body.removeChild(ta2);
        done();
      } catch (e) {
        fail();
      }
    }
  });

  /* ================================================================
     2. Salon postcode search
     --------------------------------------------------------------
     - Reads the inline JSON blob (data-salon-search-data) that the
       Liquid snippet emits next to the search box.
     - On submit: postcodes.io lookup -> compute distance to each
       salon with valid lat/lng -> render the 5 closest into the
       results column.
     - "Use my location" button hits navigator.geolocation directly.
     - Skips salons whose lat/lng came through as null.
     ============================================================== */
  var EARTH_RADIUS_KM = 6371;

  function toRad(d) { return d * Math.PI / 180; }

  function haversine(a, b) {
    var dLat = toRad(b.lat - a.lat);
    var dLng = toRad(b.lng - a.lng);
    var lat1 = toRad(a.lat);
    var lat2 = toRad(b.lat);
    var h = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
    return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
  }

  function kmToMiles(km) { return km * 0.621371; }

  function getSalonData(container) {
    var dataEl = container.querySelector('[data-salon-search-data]');
    if (!dataEl) return [];
    try {
      var parsed = JSON.parse(dataEl.textContent || '[]');
      if (!Array.isArray(parsed)) return [];
      // Drop entries with no coordinates - they can't be distance-sorted
      return parsed.filter(function (s) {
        return typeof s.lat === 'number' && typeof s.lng === 'number';
      });
    } catch (e) {
      return [];
    }
  }

  function setError(container, message) {
    var el = container.querySelector('[data-salon-search-error]');
    if (!el) return;
    if (message) {
      el.textContent = message;
      el.hidden = false;
    } else {
      el.textContent = '';
      el.hidden = true;
    }
  }

  function setHeading(container, text) {
    var el = container.querySelector('[data-salon-search-nearby-heading]');
    if (el) el.textContent = text;
  }

  function renderResults(container, salons, basePath) {
    var ul = container.querySelector('[data-salon-search-results]');
    if (!ul) return;
    if (!salons.length) {
      ul.innerHTML = '<li><p class="sc-mega__empty">No nearby salons found.</p></li>';
      return;
    }
    var base = (basePath || '/pages/salons').replace(/\/+$/, '');
    var html = salons.map(function (s) {
      var miles = (typeof s.distanceMi === 'number') ? s.distanceMi.toFixed(1) + ' mi' : (s.postcode || '');
      var label = (s.name || '').replace(/^Supercuts\s+/, '');
      return '<li>' +
        '<a href="' + base + '/' + encodeURIComponent(s.handle) + '" class="sc-mega__link" data-salon-handle="' + s.handle + '">' +
          '<span class="sc-mega__link-label">' + escapeHtml(label) + '</span>' +
          '<span class="sc-mega__link-meta">' + escapeHtml(miles) + '</span>' +
        '</a>' +
      '</li>';
    }).join('');
    ul.innerHTML = html;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function rankSalons(container, origin, headingText, basePath) {
    var salons = getSalonData(container);
    if (!salons.length) {
      setError(container, 'Salon directory not available yet.');
      return;
    }
    var ranked = salons.map(function (s) {
      var distKm = haversine(origin, { lat: s.lat, lng: s.lng });
      return Object.assign({}, s, { distanceKm: distKm, distanceMi: kmToMiles(distKm) });
    }).sort(function (a, b) { return a.distanceKm - b.distanceKm; }).slice(0, 5);

    setHeading(container, headingText);
    setError(container, '');
    renderResults(container, ranked, basePath);
  }

  function lookupPostcode(query) {
    // postcodes.io is free, no key, returns {result: {latitude, longitude}}
    var url = 'https://api.postcodes.io/postcodes/' + encodeURIComponent(query);
    return fetch(url, { credentials: 'omit' }).then(function (res) {
      if (!res.ok) throw new Error('postcode-not-found');
      return res.json();
    }).then(function (json) {
      if (!json || !json.result) throw new Error('postcode-not-found');
      return { lat: json.result.latitude, lng: json.result.longitude };
    });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-salon-search] .sc-salon-search__form');
    if (!form) return;
    event.preventDefault();

    var container = form.closest('[data-salon-search]');
    if (!container) return;
    var input = container.querySelector('[data-salon-search-input]');
    var query = (input && input.value || '').trim();
    if (!query) {
      setError(container, 'Enter a postcode to find your nearest salon.');
      return;
    }

    setError(container, 'Searching…');

    lookupPostcode(query).then(function (origin) {
      rankSalons(container, origin, 'Nearest to "' + query.toUpperCase() + '"', form.getAttribute('action'));
    }).catch(function () {
      setError(container, 'We couldn\'t find that postcode. Try a full UK postcode like SW1A 1AA.');
    });
  });

  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-salon-search-locate]');
    if (!btn) return;
    event.preventDefault();

    var container = btn.closest('[data-salon-search]');
    if (!container) return;

    if (!('geolocation' in navigator)) {
      setError(container, 'Your browser doesn\'t support location lookup. Try a postcode instead.');
      return;
    }
    setError(container, 'Finding your location…');

    var form = container.querySelector('.sc-salon-search__form');
    var basePath = form ? form.getAttribute('action') : '/pages/salons';

    navigator.geolocation.getCurrentPosition(function (pos) {
      rankSalons(container, {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude
      }, 'Nearest to you', basePath);
    }, function () {
      setError(container, 'We couldn\'t access your location. Try a postcode instead.');
    }, { timeout: 8000, maximumAge: 60000 });
  });

  /* ================================================================
     3. Lazy-load the JustGiving widget on first LPT panel open
     --------------------------------------------------------------
     The widget is a third-party script that fetches live raised/target
     totals. Loading it on every page view (just because the LPT panel
     is in the DOM) is wasteful, so we defer it until the user actually
     opens the panel.

     The Liquid snippet emits:
       <div id="jg-widget-mega-menu" data-jg-widget data-jg-widget-src="...">

     We listen for the parent <details> toggling open, then inject the
     widget's script tag once. Reusing the data-loaded flag means the
     script can never be inserted twice even if the panel is opened
     repeatedly.
     ============================================================== */
  function loadJgWidget(target) {
    if (!target || target.dataset.loaded === '1') return;
    var src = target.getAttribute('data-jg-widget-src');
    if (!src) return;
    target.dataset.loaded = '1';

    var script = document.createElement('script');
    script.src = src;
    script.async = true;
    // Insert after the target so the widget script can find its anchor
    if (target.parentNode) {
      target.parentNode.insertBefore(script, target.nextSibling);
    } else {
      document.body.appendChild(script);
    }
  }

  document.addEventListener('toggle', function (event) {
    var details = event.target;
    if (!details || details.tagName !== 'DETAILS') return;
    if (!details.open) return;
    // Only act on mega-menu details that contain a JG widget placeholder
    var widget = details.querySelector('[data-jg-widget]');
    if (widget) loadJgWidget(widget);
  }, true); // capture phase so this fires before any other toggle listener decides to do something

})();
